import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Code2, Globe, ChevronDown, CheckCircle } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import Navbar from './Navbar';
import Footer from './Footer';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  background: ${props => props.scrolled ? 'var(--nav-bg)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(12px)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid var(--glass-border)' : 'none'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const Logo = styled(Link)`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--text-main);
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const PageContainer = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: var(--bg-dark);
  color: var(--text-main);
`;

const ContentSection = styled.section`
  padding: 80px 5%;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 60px;
  align-items: center;
  margin-bottom: 100px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroImage = styled(motion.div)`
  width: 100%;
  aspect-ratio: 16/9;
  background: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: 24px;
  border: 1px solid var(--glass-border);
  box-shadow: 0 20px 40px rgba(0,0,0,0.4);
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 6vw, 4rem);
  margin-bottom: 24px;
  background: linear-gradient(to right, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.1;
`;

const Description = styled(motion.p)`
  font-size: 1.25rem;
  line-height: 1.6;
  color: var(--text-muted);
  margin-bottom: 32px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-top: 60px;
`;

const Card = styled(motion.div)`
  background: var(--bg-card);
  padding: 40px;
  border-radius: 24px;
  border: 1px solid var(--glass-border);
  transition: all 0.3s ease;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 16px;
    color: var(--text-main);
  }
  
  p {
    color: var(--text-muted);
    line-height: 1.6;
  }
  
  &:hover {
    border-color: var(--primary);
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.03);
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 40px;
  text-align: center;
  color: var(--text-main);
`;

const LangContainer = styled.div`
  position: relative;
  margin-left: 20px;
`;

const LangButton = styled.button`
  background: transparent;
  border: 1px solid var(--glass-border);
  color: var(--text-main);
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LangMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  backdrop-filter: blur(12px);
  overflow: hidden;
`;

const LangItem = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  ${props => props.active && `
    background: rgba(99, 102, 241, 0.1);
    color: var(--primary);
  `}
`;

const TechCloud = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  const techData = t('tech.cloud', { returnObjects: true });

  return (
    <>
      <Navbar />

      <PageContainer>
        <ContentSection>
          <HeroGrid>
            <div>
              <Title
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                {techData.title}
              </Title>
              <Description
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {techData.desc}
              </Description>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Link to="/contact" style={{
                  textDecoration: 'none',
                  background: 'var(--primary)',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '30px',
                  fontWeight: '600',
                  display: 'inline-block'
                }}>
                  {t('hero.cta')}
                </Link>
              </motion.div>
            </div>
            <HeroImage
              src={techData.image}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </HeroGrid>

          <SectionTitle>{t('nav.services')}</SectionTitle>
          <Grid>
            {techData.features.map((feature, index) => (
              <Card
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <h3>{feature}</h3>
                <p>{techData.benefits[index]}</p>
              </Card>
            ))}
          </Grid>
        </ContentSection>
      </PageContainer>
      <Footer />
    </>
  );
};

export default TechCloud;
